import { Booking, FamilyId, Option } from "../../../../requests-module/src";

/** Train option names: Couchette (with), Sans Couchette, Compartiment 6 pers, Sans Compartiment 6 pers */
export function isCouchetteOption(option: Option): boolean {
  const name = option?.name ?? "";
  return name.includes("Couchette") && !name.includes("Sans Couchette");
}

function isSansCouchetteOption(option: Option): boolean {
  const name = option?.name ?? "";
  return name.includes("Sans") && name.includes("Couchette");
}

export function isCompartimentOption(option: Option): boolean {
  const name = option?.name ?? "";
  return name.includes("Compartiment") && !name.includes("Sans Compartiment");
}

function isSansCompartimentOption(option: Option): boolean {
  const name = option?.name ?? "";
  return name.includes("Sans Compartiment");
}

/** Options that are train (familyId 8 or 83) for night train flow. Tolerates familyId as number or string. */
export function isTrainOption(option: Option): boolean {
  const id = Number(option.familyId);
  return id === FamilyId.TRAIN || id === FamilyId.TRAIN_SANS_COMPARTIMENT;
}

/** True when booking has at least one train option (familyId 8 or 83). Use when full 4-option set may be filtered out. */
export function hasAnyTrainOptions(booking: Booking | null): boolean {
  if (!booking?.options?.length) return false;
  return booking.options.some((opt) => isTrainOption(opt));
}

export interface NightTrainOptions {
  couchette: Option | null;
  sansCouchette: Option | null;
  compartiment: Option | null;
  sansCompartiment: Option | null;
}

/**
 * Parse the 4 night-train options from booking (FR-FR flow).
 * - Couchette: selected train with quantity (nominative)
 * - Sans Couchette: deselect Couchette (nominative)
 * - Compartiment 6 pers: private cabin (1 unit)
 * - Sans Compartiment 6 pers: deselect Compartiment (1 unit when Couchette selected)
 */
export function getNightTrainOptions(booking: Booking | null): NightTrainOptions {
  const result: NightTrainOptions = {
    couchette: null,
    sansCouchette: null,
    compartiment: null,
    sansCompartiment: null,
  };
  if (!booking?.options?.length) return result;

  const trainOpts = booking.options.filter(isTrainOption);

  for (const opt of trainOpts) {
    if (isCouchetteOption(opt)) result.couchette = opt;
    else if (isSansCouchetteOption(opt)) result.sansCouchette = opt;
    else if (isCompartimentOption(opt)) result.compartiment = opt;
    else if (isSansCompartimentOption(opt)) result.sansCompartiment = opt;
  }

  return result;
}

/** True when we have all 4 night-train options (FR-FR special flow) */
export function hasNightTrainOptions(booking: Booking | null): boolean {
  const n = getNightTrainOptions(booking);
  return !!(n.couchette && n.sansCouchette && n.compartiment && n.sansCompartiment);
}

/** True when booking has train options (8 or 83) with Couchette/Compartiment in name (night train style). Use to show night train UI even when only 2 options exist. */
export function hasNightTrainStyleOptions(booking: Booking | null): boolean {
  if (!booking?.options?.length) return false;
  return booking.options.some(
    (opt) =>
      isTrainOption(opt) &&
      !!opt.name &&
      (opt.name.includes("Couchette") || opt.name.includes("Compartiment"))
  );
}

/** True if option is a "Sans" train option (Sans Couchette or Sans Compartiment) – used to hide from selected display and package summary. */
export function isSansTrainOption(option: Option): boolean {
  const name = option?.name ?? "";
  return (
    (name.includes("Sans") && name.includes("Couchette")) ||
    name.includes("Sans Compartiment")
  );
}

/** The real selected train option to display (Couchette or Compartiment with quantity > 0). Excludes Sans options. */
export function getSelectedNightTrainOption(booking: Booking | null): Option | null {
  const n = getNightTrainOptions(booking);
  if (n.couchette && n.couchette.quantity > 0) return n.couchette;
  if (n.compartiment && n.compartiment.quantity > 0) return n.compartiment;
  return null;
}

/** Train options that are the real selection (Couchette or Compartiment), excluding Sans options. For display in summary/panel. */
export function getDisplayTrainOptions(booking: Booking | null): Option[] {
  if (!booking?.options?.length) return [];

  const activeTrainOptions = booking.options.filter(
    (opt) =>
      isTrainOption(opt) &&
      (opt.quantity ?? 0) > 0 &&
      (isCouchetteOption(opt) || isCompartimentOption(opt))
  );
  if (activeTrainOptions.length > 0) return activeTrainOptions;

  const selected = getSelectedNightTrainOption(booking);
  return selected ? [selected] : [];
}
